<template>
  <div>
    <Title :pages="[{ icon: 'clipboard-check', page: 'CONTEO CÍCLICO' }, { icon: '', page: '#' + conteo_id }]"></Title>

    <v-container fluid>
      <div class="content-main">
        <!-- ============ Cabecera ============ -->
        <v-card class="mb-4" v-if="conteo != null">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="2">
                <div class="text-caption text-medium-emphasis">Estado</div>
                <div class="status-badge font-weight-medium" :class="service.getStatusClass(conteo.status ? conteo.status.id : null)">
                  {{ conteo.status ? conteo.status.name : "-" }}
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text-medium-emphasis">Tipo</div>
                <div>{{ conteo.type ? conteo.type.name : "-" }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text-medium-emphasis">Almacén</div>
                <div>{{ conteo.almacen ? conteo.almacen.name : "-" }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text-medium-emphasis">Cliente</div>
                <div>{{ conteo.client ? conteo.client.name : "Todos" }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text-medium-emphasis">Creado</div>
                <div>{{ conteo.creation_date }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text-medium-emphasis">Cerrado</div>
                <div>{{ conteo.close_date || "-" }}</div>
              </v-col>
            </v-row>
            <v-row v-if="conteo.observations">
              <v-col cols="12">
                <div class="text-caption text-medium-emphasis">Observaciones</div>
                <div>{{ conteo.observations }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ============ Reporte ============ -->
        <v-card class="mb-4" v-if="reporte != null">
          <v-card-title class="text-h6">Reporte</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4" v-for="tile in tiles" :key="tile.label" class="d-flex">
                <div class="stat-tile pa-3">
                  <div class="text-caption text-medium-emphasis">{{ tile.label }}</div>
                  <div class="text-h5">{{ tile.value }}</div>
                  <!-- El hint siempre ocupa lugar (aunque este vacio) para que las cards midan igual -->
                  <div class="text-caption text-medium-emphasis stat-tile-hint">{{ tile.hint }}</div>
                </div>
              </v-col>
            </v-row>

            <!--
              La neta sola engaña: un faltante de 100 con un sobrante de 100 da cero y ahí hay
              dos errores, no ninguno. Por eso van siempre las dos juntas y con esta aclaración.
            -->
            <v-alert
              v-if="reporte.diferencia_neta === 0 && reporte.diferencia_absoluta > 0"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              La diferencia neta da cero porque los faltantes y los sobrantes se compensan, pero hay
              <b>{{ reporte.diferencia_absoluta }}</b> unidades de diferencia real.
            </v-alert>
          </v-card-text>
        </v-card>

        <!--
          ============ Ubicaciones y sus diferencias ============
          Una sola vista: antes eran dos pestañas (ubicaciones / líneas sueltas) y el revisor
          tenía que emparejar a mano una línea con su ubicación para poder aplicar el ajuste,
          que se hace POR UBICACIÓN. Acá cada fila de la tabla es una ubicación y sus diferencias
          se ven y se resuelven en un modal (ModalLineasUbicacion), que es como se decide en la realidad.
        -->
        <v-card>
          <!--
            Titulo y expandir a la izquierda, buscar y exportar a la derecha, todo en un renglon.
            Uno debajo del otro duplicaba el alto de la cabecera sin ganar nada: el boton de
            exportar es corto y entra al lado del buscador.
          -->
          <v-card-title class="cabecera-tabla">
            <v-row align="center" class="pt-2 pb-2">
              <v-col cols="12" md="6">
                <div>
                  <span class="text-h6">Ubicaciones y diferencias</span>
                  <div class="text-caption text-grey-600">{{ resumen_grupos }}</div>
                </div>
              </v-col>

              <v-spacer></v-spacer>

              <v-col cols="12" md="5">
                <div class="d-flex align-center ga-2">
                  <v-text-field
                    v-model="search"
                    append-inner-icon="mdi-magnify"
                    single-line
                    hide-details
                    label="Buscar ubicación o zona"
                    density="compact"
                    class="flex-grow-1"
                  ></v-text-field>
                  <v-btn variant="text" prepend-icon="mdi-download" size="small" class="flex-shrink-0" @click="exportar" :loading="loading_export">Exportar detalle</v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-title>

          <v-card-text>
            <div class="d-flex align-end flex-wrap ga-4 mb-2">
              <!-- Entre parentesis, cuantas ubicaciones del conteo caen en cada opcion -->
              <div>
                <div class="text-caption font-weight-medium text-grey-darken-3 mb-1">Pendientes</div>
                <div class="filtro-segmentado">
                  <button v-for="op in filtro_estado_opciones" :key="op.id" type="button" :class="{ active: filtro_estado === op.id }" @click="filtro_estado = op.id">
                    {{ op.name }} ({{ cantidad_por_estado[op.id === null ? "todos" : op.id] || 0 }})
                  </button>
                </div>
              </div>
              <div>
                <div class="text-caption font-weight-medium text-grey-darken-3 mb-1">Diferencias</div>
                <div class="filtro-segmentado">
                  <button v-for="op in filtro_resolucion_opciones" :key="op.id" type="button" :class="{ active: filtro_resolucion === op.id }" @click="filtro_resolucion = op.id">
                    {{ op.name }} ({{ cantidad_por_resolucion[op.id === null ? "todas" : op.id] || 0 }})
                  </button>
                </div>
              </div>
            </div>
            <div>
                <v-alert v-if="hay_marcadas_sin_aplicar" type="info" variant="tonal" density="compact" class="mb-3">
                  Hay líneas marcadas que <b>todavía no se ajustaron</b>. El ajuste se aplica por ubicación, con el botón <b>“Aplicar ajustes”</b> de la fila de cada ubicación
                  o tildando varias ubicaciones y usando el menú de acciones.
                </v-alert>
            </div>

            <!-- Acciones masivas sobre las ubicaciones tildadas, como en ShipmentOrders.vue -->
            <div class="mb-3" v-if="update_access && ubicaciones_seleccionables.length > 0">
              <v-menu offset-y>
                <template v-slot:activator="{ props }">
                  <v-icon style="border: 1px solid; border-radius: 100px; padding: 3px" color="blue-grey" :class="selected.length > 0 ? 'button_blinking' : ''" v-bind="props">
                    mdi-dots-vertical
                  </v-icon>
                </template>

                <v-list>
                  <v-list-item @click="abrirAplicarSeleccion">
                    <v-list-item-title>Aplicar ajustes</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="conteo_abierto" @click="abrirReconteoSeleccion">
                    <v-list-item-title>Pedir segundo conteo</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <span v-if="selected.length > 0" class="text-caption text-grey-600 ml-2">{{ selected.length }} seleccionadas</span>
            </div>

            <v-data-table
              :headers="headers"
              :items="grupos"
              :loading="loading_ubicaciones"
              loading-text="Cargando..."
              no-data-text="No hay ubicaciones para mostrar con los filtros actuales."
              item-value="id"
              :items-per-page="50"
              class="tabla-recuadro"
            >
              <template v-slot:[`header.select`]>
                <v-checkbox
                  style="margin: 0px; padding: 0px"
                  hide-details
                  density="compact"
                  :disabled="ubicaciones_seleccionables.length === 0"
                  :model-value="todas_seleccionadas"
                  :indeterminate="selected.length > 0 && !todas_seleccionadas"
                  @update:modelValue="toggleSelectAll"
                />
              </template>
              <template v-slot:item="{ item }">
                <tr class="ubicacion-row">
                  <!-- Solo se tildan las que admiten alguna acción masiva: aplicar ajustes o pedir el segundo conteo -->
                  <td class="col-check" v-if="update_access">
                    <v-checkbox
                      :disabled="!esSeleccionable(item)"
                      style="margin: 0px; padding: 0px"
                      hide-details
                      density="compact"
                      :model-value="selected.includes(item.id)"
                      @update:modelValue="toggleSelect(item)"
                    />
                  </td>
                  <td>
                    <div class="font-weight-bold nowrap">{{ item.code }}</div>
                    <span class="text-caption text-grey-600">{{ item.zona }}</span>
                  </td>
                  <td>
                    <div class="status-badge font-weight-medium" :class="service.getLocationStatusClass(item.ubicacion.status ? item.ubicacion.status.id : null)">
                      {{ item.ubicacion.status ? item.ubicacion.status.name : "-" }}
                    </div>
                    <v-chip v-if="item.count_number === 2" color="deep-purple" size="x-small" variant="tonal" class="ml-2">2° conteo</v-chip>
                  </td>
                  <td>
                    <v-chip v-if="item.pendientes > 0" color="orange" size="small" class="mr-1">{{ item.pendientes }} sin resolver</v-chip>
                    <v-chip v-if="item.marcadas > 0" color="green" size="small" class="mr-1">{{ item.marcadas }} marcadas</v-chip>
                    <span v-if="item.con_diferencia === 0" class="text-caption text-grey-600">Sin diferencias</span>
                    <v-chip v-else-if="item.pendientes === 0 && item.marcadas === 0" color="blue-grey" size="small" variant="tonal">{{ item.con_diferencia }} resueltas</v-chip>
                  </td>
                  <!-- Fecha y operario de cada pasada: salen de las tareas, la ubicacion solo guarda la ultima -->
                  <td>
                    <div class="nowrap">{{ item.conteo_1 ? item.conteo_1.count_date : "-" }}</div>
                    <span class="text-caption text-grey-600">{{ item.conteo_1 && item.conteo_1.user ? item.conteo_1.user.name : "-" }}</span>
                  </td>
                  <td>
                    <div class="nowrap">{{ item.conteo_2 ? item.conteo_2.count_date : "-" }}</div>
                    <span class="text-caption text-grey-600">{{ item.conteo_2 && item.conteo_2.user ? item.conteo_2.user.name : "-" }}</span>
                  </td>
                  <td class="text-center">
                    <v-tooltip bottom v-if="item.ubicacion.has_reserved_stock">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="orange" size="small" class="mr-1">mdi-lock-alert</v-icon>
                      </template>
                      <span>Tenía stock reservado para un pedido al momento de emitir</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.ubicacion.outdated_theoretical">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="red" size="small">mdi-clock-alert</v-icon>
                      </template>
                      <span>El stock se movió por debajo entre el congelado y el cierre: la diferencia puede ser falsa</span>
                    </v-tooltip>
                  </td>
                  <td class="text-center nowrap">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" size="x-small" color="blue" variant="tonal" class="font-weight-bold mr-2" @click="abrirUbicacion(item)">Ver conteo</v-btn>
                      </template>
                      <span>Ver las líneas de la ubicación</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="esRecontable(item)">
                      <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" size="x-small" color="deep-purple" variant="tonal" class="font-weight-bold ml-2 mr-2" @click.stop="abrirReconteo([item.ubicacion])">2° conteo</v-btn>
                      </template>
                      <span>Pedir un segundo conteo ciego de la ubicación entera, antes de decidir</span>
                    </v-tooltip>

                    <!-- Aprobar es por UBICACION, no por linea: el ajuste necesita el desbloqueo primero -->
                    <v-tooltip bottom v-if="update_access && puedeAprobarUbicacion(item.ubicacion)">
                      <template v-slot:activator="{ props }">
                        <!--
                          El activator va en el span y no en el boton: Vuetify le pone
                          pointer-events:none al v-btn deshabilitado, y sin esto el tooltip que
                          explica POR QUE esta deshabilitado es justo el que no se puede leer.
                        -->
                        <span v-bind="props">
                          <v-btn
                            size="x-small"
                            color="blue-grey"
                            :variant="item.marcadas > 0 ? 'elevated' : 'tonal'"
                            :disabled="item.marcadas === 0"
                            class="font-weight-bold"
                            @click.stop="confirmarAprobarUbicacion(item.ubicacion)"
                            >Aplicar ajustes</v-btn
                          >
                        </span>
                      </template>
                      <span v-if="item.marcadas > 0">Desbloquea la ubicación y aplica los ajustes de las líneas marcadas</span>
                      <span v-else>No hay líneas marcadas para ajustar en esta ubicación</span>
                    </v-tooltip>

          

                    <v-tooltip bottom v-if="esReconteoCancelable(item)">
                      <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" size="x-small" color="red" variant="tonal" class="font-weight-bold ml-2" @click.stop="abrirCancelarReconteo(item.ubicacion)">Cancelar 2° conteo</v-btn>
                      </template>
                      <span>Cancela la tarea del segundo conteo y vuelve a dejar vigente el primero</span>
                    </v-tooltip>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!--
          Las lineas de cada ubicacion y sus acciones. Antes era un accordion en la tabla; el
          descarte y el aplicar ajustes se abren encima de este modal.
        -->
        <ModalLineasUbicacion
          ref="modalLineas"
          :dialog="dialog_ubicacion"
          :stock_count_location_id="ubicacion_modal_id"
          :code="ubicacion_modal_code"
          :update_access="update_access"
          :conteo_abierto="conteo_abierto"
          @close="cerrarUbicacion"
          @aprobar="abrirAprobar"
          @descartar="abrirDescartar"
          @revertir="revertirDescarte"
          @aplicar="confirmarAprobarUbicacion"
          @recontar="abrirReconteo"
          @cancelar-reconteo="abrirCancelarReconteo"
        />

        <!-- Marcar para ajustar: se confirma antes, sea una línea o la selección masiva -->
        <DialogYesNo
          :open="dialog_marcar"
          :loading="loading_accion"
          title="Marcar para ajustar"
          :message="mensaje_marcar"
          @cancel="dialog_marcar = false"
          @accept="aprobarLineas"
        />

        <!-- Aplicar ajustes masivo: cada ubicación se desbloquea y se ajusta por su cuenta -->
        <DialogYesNo
          :open="dialog_aplicar_seleccion"
          :loading="loading_accion"
          title="Aplicar ajustes"
          :message="mensaje_aplicar_seleccion"
          @cancel="dialog_aplicar_seleccion = false"
          @accept="aplicarSeleccion"
        />

        <!-- Descartar: el motivo es obligatorio -->
        <v-dialog v-model="dialog_descartar" max-width="500px">
          <v-card>
            <v-card-title class="headline">{{ lineas_a_descartar.length > 1 ? "Descartar " + lineas_a_descartar.length + " diferencias" : "Descartar la diferencia" }}</v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-3">
                {{ lineas_a_descartar.length > 1 ? "Las líneas quedan registradas como “diferencia descartada”, todas con el mismo motivo," : "La línea queda registrada como “diferencia descartada”," }}
                sin ajustar el stock. El motivo es obligatorio: es lo único que después explica por qué no se ajustó.
              </p>
              <v-textarea v-model="motivo_descarte" label="Motivo*" rows="3" auto-grow></v-textarea>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="dialog_descartar = false">Cancelar</v-btn>
              <v-btn color="red darken-1" text @click="descartarLineas" :loading="loading_accion" :disabled="!motivo_descarte">Descartar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Segundo conteo: solo se confirma, sin motivo -->
        <v-dialog v-model="dialog_reconteo" max-width="500px">
          <v-card>
            <v-card-title class="headline">{{ ubicaciones_a_recontar.length > 1 ? "Pedir segundo conteo de " + ubicaciones_a_recontar.length + " ubicaciones" : "Pedir segundo conteo" }}</v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-3">
                Se emite una tarea nueva para contar de nuevo, a ciegas, la ubicación entera. Sigue bloqueada y con el mismo teórico. Cuando se cierre, si coincide con el teórico se cierra sola;
                si no, vuelve a revisión con los dos conteos a la vista. No hay tercer conteo.
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="dialog_reconteo = false">Cancelar</v-btn>
              <v-btn color="deep-purple" text @click="pedirReconteo" :loading="loading_accion">Pedir segundo conteo</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Cancelar el segundo conteo: solo se confirma -->
        <DialogYesNo
          :open="dialog_cancelar_reconteo"
          :loading="loading_accion"
          title="Cancelar segundo conteo"
          :message="mensaje_cancelar_reconteo"
          @cancel="dialog_cancelar_reconteo = false"
          @accept="cancelarReconteo"
        />

        <!--
          Aplicar ajustes NO es un si/no: cada linea marcada puede rebotar por su cuenta y el
          revisor necesita ver cual. La accion corre adentro del modal y el resultado aparece
          sobre la fila, como en ModalReservarStock.
        -->
        <ModalAplicarAjustes
          :dialog="dialog_aprobar"
          :code="ubicacion_a_aprobar != null && ubicacion_a_aprobar.ubicacion != null ? ubicacion_a_aprobar.ubicacion.code : ''"
          :lineas="lineas_a_aprobar"
          :loading="loading_accion"
          :confirm="aprobarUbicacion"
          @close="cerrarAplicarAjustes"
        />
      </div>
    </v-container>
  </div>
</template>

<script>
import Title from "@/components/Title.vue";
import ModalAplicarAjustes from "./ModalAplicarAjustes.vue";
import ModalLineasUbicacion from "./ModalLineasUbicacion.vue";
import { conteoService } from "@/libs/ws/conteoService";
import Constant from "@/constants/sections";
import RolMixin from "@/mixins/RolMixin.vue";
import AliasMixin from "@/mixins/AliasMixin.vue";
import * as XLSX from "xlsx";

export default {
  components: { Title, ModalAplicarAjustes, ModalLineasUbicacion },
  mixins: [RolMixin, AliasMixin],
  data() {
    return {
      rol_section: Constant.SECTION_CONTEO,
      service: conteoService,

      conteo: null,
      reporte: null,
      //Cada una con sus contadores de revision (pendientes, marcadas, con_diferencia). Las
      //lineas NO se piden aca: las trae el modal al abrir la ubicacion (list-lineas-ubicacion).
      ubicaciones: [],

      search: "",

      ubicaciones_headers: [
        { title: "Ubicación", key: "code", sortable: true, align: "start" },
        { title: "Estado", key: "ubicacion.status.name", sortable: true, align: "start" },
        { title: "Diferencias", key: "pendientes", sortable: true, align: "start" },
        { title: "Conteo 1", key: "conteo_1.count_date", sortable: true, align: "start" },
        { title: "Conteo 2", key: "conteo_2.count_date", sortable: true, align: "start" },
        { title: "", key: "marcas", sortable: false, align: "center" },
        { title: "Acción", key: "actions", sortable: false, align: "center" },
      ],

      loading: false,
      loading_ubicaciones: false,
      loading_export: false,
      loading_accion: false,

      //Filtros rapidos de la tabla. null = todos
      filtro_estado: null,
      filtro_estado_opciones: [
        { id: null, name: "Todos" },
        ...[
          conteoService.LOCATION_PENDIENTE,
          conteoService.LOCATION_CONTADA,
          conteoService.LOCATION_CON_DIFERENCIA,
          conteoService.LOCATION_RECONTEO,
          conteoService.LOCATION_CERRADA,
          conteoService.LOCATION_EXCLUIDA,
        ].map((id) => ({
          id: id,
          name: conteoService.getLocationStatusName(id),
        })),
      ],
      filtro_resolucion: null,
      filtro_resolucion_opciones: [
        { id: null, name: "Todas" },
        { id: "sin_resolver", name: "Sin resolver" },
        { id: "resueltas", name: "Resueltas" },
      ],

      //Ids de las ubicaciones tildadas para las acciones masivas (aplicar ajustes, segundo conteo)
      selected: [],
      dialog_aplicar_seleccion: false,

      dialog_reconteo: false,
      dialog_cancelar_reconteo: false,
      ubicacion_a_cancelar_reconteo: null,
      //StockCountLocation (el objeto de la fila, con su id): una, o las tildadas
      ubicaciones_a_recontar: [],

      dialog_marcar: false,
      lineas_a_marcar: [],

      dialog_descartar: false,
      motivo_descarte: "",
      //Una o varias (seleccion masiva del modal de lineas): el motivo es el mismo para todas
      lineas_a_descartar: [],

      //Se guarda el id y no el grupo: el grupo sale de un computed y tiene que reflejar cada recarga
      dialog_ubicacion: false,
      ubicacion_modal_id: null,
      ubicacion_modal_code: "",

      dialog_aprobar: false,
      ubicacion_a_aprobar: null,
      //Las marcadas al momento de abrir el modal. No es acumulable: si despues se marcan mas,
      //el modal se vuelve a abrir con esas.
      lineas_a_aprobar: [],
    };
  },
  computed: {
    conteo_id() {
      return this.$route.params.id;
    },
    headers() {
      if (!this.update_access) return this.ubicaciones_headers;
      return [{ title: "", key: "select", sortable: false, align: "start", width: "48px" }, ...this.ubicaciones_headers];
    },
    /** Las visibles con los filtros actuales que se pueden aplicar. */
    ubicaciones_seleccionables() {
      return this.grupos.filter((g) => this.esSeleccionable(g));
    },
    todas_seleccionadas() {
      return this.ubicaciones_seleccionables.length > 0 && this.ubicaciones_seleccionables.every((g) => this.selected.includes(g.id));
    },
    ubicaciones_seleccionadas() {
      return this.ubicaciones_seleccionables.filter((g) => this.selected.includes(g.id));
    },
    //Cada accion masiva toma, de lo tildado, solo lo que le corresponde
    seleccionadas_aplicables() {
      return this.ubicaciones_seleccionadas.filter((g) => this.esAplicable(g));
    },
    seleccionadas_recontables() {
      return this.ubicaciones_seleccionadas.filter((g) => this.esRecontable(g));
    },
    mensaje_aplicar_seleccion() {
      const n = this.seleccionadas_aplicables.length;
      const lineas = this.seleccionadas_aplicables.reduce((acc, g) => acc + g.marcadas, 0);
      return "¿Está seguro que quiere aplicar los ajustes de " + lineas + " líneas marcadas en " + (n === 1 ? "1 ubicación" : n + " ubicaciones") + "? Cada ubicación se desbloquea y se ajusta el stock.";
    },
    hay_marcadas_sin_aplicar() {
      return this.ubicaciones.some((u) => u.marcadas > 0);
    },
    /** Una fila por ubicacion con sus contadores. El buscador es solo por ubicacion o zona. */
    grupos() {
      const q = (this.search || "").toLowerCase().trim();

      return this.ubicaciones
        .map((u) => this.armarGrupo(u))
        .filter((g) => {
          if (this.filtro_estado != null && (g.ubicacion.status ? g.ubicacion.status.id : null) !== this.filtro_estado) return false;
          if (this.filtro_resolucion != null && this.resolucionDe(g) !== this.filtro_resolucion) return false;
          return q === "" || g.code.toLowerCase().includes(q) || g.zona.toLowerCase().includes(q);
        });
    },
    /** Cuantas ubicaciones hay en cada estado, sobre todo el conteo (sin filtros). */
    cantidad_por_estado() {
      const result = { todos: this.ubicaciones.length };
      this.ubicaciones.forEach((u) => {
        const id = u.status ? u.status.id : null;
        result[id] = (result[id] || 0) + 1;
      });
      return result;
    },
    /** Cuantas ubicaciones hay sin resolver y resueltas, sobre todo el conteo (sin filtros). */
    cantidad_por_resolucion() {
      const result = { todas: this.ubicaciones.length };
      this.ubicaciones.forEach((u) => {
        const resolucion = this.resolucionDe(this.armarGrupo(u));
        if (resolucion != null) result[resolucion] = (result[resolucion] || 0) + 1;
      });
      return result;
    },
    mensaje_cancelar_reconteo() {
      const u = this.ubicacion_a_cancelar_reconteo;
      const code = u && u.ubicacion ? u.ubicacion.code : "";
      return "¿Está seguro que quiere cancelar el segundo conteo de la ubicación " + code + "? Se cancela la tarea, se descarta lo que el operario haya cargado y vuelve a quedar vigente el primer conteo.";
    },
    mensaje_marcar() {
      if (this.lineas_a_marcar.length > 1) return "¿Está seguro que quiere marcar para ajustar las " + this.lineas_a_marcar.length + " líneas seleccionadas?";
      const linea = this.lineas_a_marcar[0];
      return "¿Está seguro que quiere marcar para ajustar el producto " + (linea ? linea.producto_name : "") + "?";
    },
    conteo_abierto() {
      return this.conteo != null && this.service.isAbierto(this.conteo.status ? this.conteo.status.id : null);
    },
    resumen_grupos() {
      const con_pendientes = this.grupos.filter((g) => g.pendientes > 0).length;
      return this.grupos.length + " ubicaciones · " + con_pendientes + " con diferencias sin resolver";
    },
    tiles() {
      if (this.reporte == null) return [];
      const r = this.reporte;
      return [
        { label: "Avance", value: r.porcentaje_avance + "%", hint: r.ubicaciones_contadas + " de " + (r.ubicaciones_total - r.ubicaciones_excluidas) + " ubicaciones" },
        { label: "Exactitud por línea", value: r.porcentaje_exactitud + "%", hint: r.lineas_ok + " de " + r.lineas_contadas + " líneas sin diferencia" },
        //El estandar de la industria es este, no el de linea: por eso va con su nombre completo
        { label: "Exactitud por ubicación", value: r.porcentaje_exactitud_ubicacion + "%", hint: r.ubicaciones_sin_diferencia + " ubicaciones sin ninguna diferencia" },
      ];
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    volver() {
      this.$router.push("/" + Constant.SECTION_CONTEO);
    },
    loadAll() {
      this.loadConteo();
      this.loadReporte();
      this.loadUbicaciones();
    },
    loadConteo() {
      this.loading = true;
      this.service
        .get({ id: this.conteo_id })
        .then((response) => {
          if (response.status) this.conteo = response.result;
          else this.toast.error(response.msg);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadReporte() {
      this.service.reporte({ id: this.conteo_id }).then((response) => {
        if (response.status) this.reporte = response.result;
      });
    },
    loadUbicaciones() {
      this.loading_ubicaciones = true;
      this.service
        .list_ubicaciones({ id: this.conteo_id, has_pagination: false, include_contadores: true })
        .then((response) => {
          if (response.status) {
            this.ubicaciones = response.result.list;
            //Las que ya no se pueden aplicar (se aplicaron, se desmarcaron) salen de la seleccion
            const seleccionables = this.ubicaciones.map((u) => this.armarGrupo(u)).filter((g) => this.esSeleccionable(g));
            this.selected = this.selected.filter((id) => seleccionables.some((g) => g.id === id));
          }
        })
        .finally(() => {
          this.loading_ubicaciones = false;
        });
    },
    /**
     * Despues de una accion sobre lineas: los contadores de la tabla y, si esta abierto, el modal
     * con las lineas de su ubicacion. Con recargar_todo tambien cabecera y reporte, que cambian
     * cuando una linea se resuelve.
     */
    recargarRevision(recargar_todo = false) {
      if (recargar_todo) this.loadAll();
      else this.loadUbicaciones();

      if (this.dialog_ubicacion && this.$refs.modalLineas) this.$refs.modalLineas.cargar();
    },
    abrirUbicacion(grupo) {
      this.ubicacion_modal_id = grupo.id;
      this.ubicacion_modal_code = grupo.code;
      this.dialog_ubicacion = true;
    },
    cerrarUbicacion() {
      this.dialog_ubicacion = false;
      this.ubicacion_modal_id = null;
      this.ubicacion_modal_code = "";
    },
    /**
     * Una fila por ubicacion. Los contadores vienen del backend (StockCountLocation::
     * GetContadoresRevision), con el mismo criterio que AprobarAjustes(): marcada = approved Y
     * todavia pendiente, porque SellarAjuste() no apaga approved cuando el ajuste sale bien.
     */
    /**
     * Sin resolver = le queda alguna diferencia esperando decision o el ajuste (marcada).
     * Resuelta = tuvo diferencias y ya no le queda ninguna. Las que nunca tuvieron: null.
     */
    resolucionDe(g) {
      if (g.pendientes > 0 || g.marcadas > 0) return "sin_resolver";
      if (g.con_diferencia > 0) return "resueltas";
      return null;
    },
    armarGrupo(u) {
      return {
        id: u.id,
        ubicacion: u,
        code: u.ubicacion ? u.ubicacion.code : "-",
        zona: u.ubicacion && u.ubicacion.zona ? u.ubicacion.zona.name + (u.ubicacion.zona.almacen ? " - " + u.ubicacion.zona.almacen.code : "") : "-",
        pendientes: u.pendientes || 0,
        marcadas: u.marcadas || 0,
        con_diferencia: u.con_diferencia || 0,
        count_number: u.count_number || 1,
        puede_recontar: u.puede_recontar === true,
        conteo_1: this.armarConteo(u, 1),
        conteo_2: this.armarConteo(u, 2),
      };
    },
    /**
     * Fecha y operario de una pasada, de las tareas finalizadas (backend: conteos). Sin la tarea,
     * el primer conteo cae a lo que guarda la ubicacion, que solo vale mientras no hubo segundo.
     */
    armarConteo(u, count_number) {
      const conteo = u.conteos ? u.conteos[count_number] : null;
      if (conteo) return conteo;
      if (count_number === 1 && (u.count_number || 1) === 1 && u.count_date) return { count_date: u.count_date, user: u.user_count };
      return null;
    },

    // --- Presentacion de la linea (solo para el Excel: el modal recibe las lineas ya armadas)
    /**
     * De donde salen producto y atributos: la tupla de la linea o, en un hallazgo con una serie o
     * un lote que el sistema todavia no conoce, lo que declaro el operario (el backend lo arma del
     * scan con la misma forma). Sin esto esa fila mostraba solo el LPN, y el revisor marcaba para
     * ajustar algo que no podia ver.
     */
    fuenteAtributos(item) {
      const pa = item.presentation_atributes;
      if (pa != null) return { ...pa, product: pa.product_presentation ? pa.product_presentation.product : null };
      return item.declarado || null;
    },
    productoName(item) {
      const f = this.fuenteAtributos(item);
      return f && f.product ? f.product.name : "-";
    },
    productoSku(item) {
      const f = this.fuenteAtributos(item);
      return f && f.product ? f.product.sku : "-";
    },
    /** Los atributos que la linea realmente tiene cargados; los descriptivos, con su alias. */
    atributos(item) {
      const f = this.fuenteAtributos(item);
      if (f == null) return [];

      const attrs = [];
      if (f.lote) attrs.push({ label: "Lote", value: f.lote });
      if (f.serie) attrs.push({ label: "Serie", value: f.serie });
      if (f.expiration_date) attrs.push({ label: "Vencimiento", value: f.expiration_date });
      if (f.storage_units_amount) {
        //La unidad viaja como objeto (product.storage_unit.name), no como string plano
        const unidad = f.product && f.product.storage_unit ? f.product.storage_unit.name : "";
        attrs.push({ label: "Almacenamiento", value: f.storage_units_amount + (unidad ? " " + unidad : "") });
      }
      if (f.quality && f.quality.id) attrs.push({ label: "Calidad", value: f.quality.name });

      ["descriptivo_1", "descriptivo_2", "descriptivo_3"].forEach((key) => {
        if (this.isAliasEnabled(key) && f[key]) attrs.push({ label: this.getAliasName(key), value: f[key] });
      });

      return attrs;
    },

    // --- Revision
    /**
     * Las acciones por linea NO avisan con toast cuando salen bien: el cambio se ve solo en la
     * fila —aparece el chip, cambia el badge de estado— y el cartel tapaba justo eso. El error
     * si va, que es lo unico que la tabla no puede mostrar por su cuenta.
     */
    abrirAprobar(lineas) {
      this.lineas_a_marcar = lineas;
      this.dialog_marcar = true;
    },
    aprobarLineas() {
      if (this.lineas_a_marcar.length === 0) return;

      this.loading_accion = true;
      this.service
        .aprobar_linea({ stock_count_line_ids: this.lineas_a_marcar.map((l) => l.id) })
        .then((response) => {
          if (response.status) {
            this.dialog_marcar = false;
            this.avisarRechazos(response);
            this.recargarRevision();
          } else {
            this.toast.error(response.msg);
          }
        })
        .finally(() => {
          this.loading_accion = false;
        });
    },
    /** En la seleccion masiva la que no se pudo resolver no frena al resto: se avisa cual fue. */
    avisarRechazos(response) {
      const errores = response.result && response.result.errores ? response.result.errores : [];
      if (errores.length > 0) this.toast.warning(errores.join(" / "));
    },
    revertirDescarte(item) {
      this.service.revertir_descarte_linea({ stock_count_line_id: item.id }).then((response) => {
        if (response.status) this.recargarRevision(true);
        else this.toast.error(response.msg);
      });
    },
    abrirDescartar(lineas) {
      this.lineas_a_descartar = lineas;
      this.motivo_descarte = "";
      this.dialog_descartar = true;
    },
    descartarLineas() {
      if (this.lineas_a_descartar.length === 0 || !this.motivo_descarte) return;

      this.loading_accion = true;
      this.service
        .descartar_linea({ stock_count_line_ids: this.lineas_a_descartar.map((l) => l.id), motivo: this.motivo_descarte })
        .then((response) => {
          if (response.status) {
            this.dialog_descartar = false;
            this.avisarRechazos(response);
            this.recargarRevision(true);
          } else {
            this.toast.error(response.msg);
          }
        })
        .finally(() => {
          this.loading_accion = false;
        });
    },

    puedeAprobarUbicacion(item) {
      return item.status != null && item.status.id === this.service.LOCATION_CON_DIFERENCIA;
    },

    // --- Acciones masivas
    esAplicable(grupo) {
      return this.update_access && this.puedeAprobarUbicacion(grupo.ubicacion) && grupo.marcadas > 0;
    },
    /** puede_recontar lo calcula el backend con las mismas guardas que el pedido. */
    esRecontable(grupo) {
      return this.update_access && this.conteo_abierto && grupo.puede_recontar === true;
    },
    /** Mientras espera el segundo conteo: si la tarea ya se cerró, la ubicación ya no está en REC. */
    esReconteoCancelable(grupo) {
      const status = grupo.ubicacion.status;
      return this.update_access && this.conteo_abierto && status != null && status.id === this.service.LOCATION_RECONTEO;
    },
    esSeleccionable(grupo) {
      return this.esAplicable(grupo) || this.esRecontable(grupo);
    },
    toggleSelect(grupo) {
      if (this.selected.includes(grupo.id)) this.selected = this.selected.filter((id) => id !== grupo.id);
      else this.selected = [...this.selected, grupo.id];
    },
    toggleSelectAll(isSelected) {
      this.selected = isSelected ? this.ubicaciones_seleccionables.map((g) => g.id) : [];
    },
    abrirAplicarSeleccion() {
      if (this.seleccionadas_aplicables.length === 0) {
        this.toast.error("Ninguna de las ubicaciones seleccionadas tiene líneas marcadas para ajustar");
        return;
      }
      this.dialog_aplicar_seleccion = true;
    },
    abrirReconteoSeleccion() {
      if (this.seleccionadas_recontables.length === 0) {
        this.toast.error("Ninguna de las ubicaciones seleccionadas admite un segundo conteo");
        return;
      }
      this.abrirReconteo(this.seleccionadas_recontables.map((g) => g.ubicacion));
    },

    // --- Segundo conteo
    abrirReconteo(ubicaciones) {
      this.ubicaciones_a_recontar = ubicaciones;
      this.dialog_reconteo = true;
    },
    /** Todo o nada en el backend: si una no se puede recontar, no sale ninguna y vuelve el porque. */
    pedirReconteo() {
      if (this.ubicaciones_a_recontar.length === 0) return;

      this.loading_accion = true;
      this.service
        .pedir_reconteo({ stock_count_location_ids: this.ubicaciones_a_recontar.map((u) => u.id) })
        .then((response) => {
          if (response.status) {
            this.toast.success(response.msg);
            this.dialog_reconteo = false;
            this.selected = [];
            //Cambia la ubicacion, sus lineas, el reporte y quizas la cabecera (REV -> PRO)
            this.recargarRevision(true);
          } else {
            this.toast.error(response.msg);
          }
        })
        .finally(() => {
          this.loading_accion = false;
        });
    },
    abrirCancelarReconteo(ubicacion) {
      this.ubicacion_a_cancelar_reconteo = ubicacion;
      this.dialog_cancelar_reconteo = true;
    },
    cancelarReconteo() {
      if (this.ubicacion_a_cancelar_reconteo == null) return;

      this.loading_accion = true;
      this.service
        .cancelar_reconteo({ stock_count_location_id: this.ubicacion_a_cancelar_reconteo.id })
        .then((response) => {
          if (response.status) {
            this.toast.success(response.msg);
            this.dialog_cancelar_reconteo = false;
            this.ubicacion_a_cancelar_reconteo = null;
            //Cambia la ubicacion, sus lineas, el reporte y quizas la cabecera (PRO -> REV)
            this.recargarRevision(true);
          } else {
            this.toast.error(response.msg);
          }
        })
        .finally(() => {
          this.loading_accion = false;
        });
    },
    /**
     * Cada ubicacion se aplica por su cuenta en el backend: la que rebota no frena al resto. Aca
     * no hay detalle linea por linea como en ModalAplicarAjustes; lo que rebotó se avisa y queda
     * a la vista en la tabla (la ubicación sigue con líneas sin resolver).
     */
    aplicarSeleccion() {
      if (this.seleccionadas_aplicables.length === 0) return;

      this.loading_accion = true;
      this.service
        .aprobar_ubicacion({ stock_count_location_ids: this.seleccionadas_aplicables.map((g) => g.id) })
        .then((response) => {
          if (response.status) {
            this.toast.success(response.msg);
            this.avisarRechazos(response);
          } else {
            this.toast.error(response.msg);
          }
        })
        .finally(() => {
          this.dialog_aplicar_seleccion = false;
          this.loading_accion = false;
          this.selected = [];
          //Se recarga siempre: aunque los ajustes reboten, las ubicaciones ya se desbloquearon
          this.loadAll();
        });
    },
    /**
     * Desde el modal de lineas llegan las marcadas ya armadas por el backend. Desde la fila de la
     * tabla se piden las lineas de esa ubicacion en el momento: la pantalla ya no las tiene.
     */
    confirmarAprobarUbicacion(item, lineas_marcadas = null) {
      if (lineas_marcadas != null) {
        this.ubicacion_a_aprobar = item;
        this.lineas_a_aprobar = lineas_marcadas;
        this.dialog_aprobar = true;
        return;
      }

      this.loading_accion = true;
      this.service
        .list_lineas_ubicacion({ stock_count_location_id: item.id })
        .then((response) => {
          if (!response.status) {
            this.toast.error(response.msg);
            return;
          }
          this.ubicacion_a_aprobar = item;
          this.lineas_a_aprobar = response.result.lineas.filter((l) => l.approved === true && this.service.isPendienteDeResolucion(l.status ? l.status.id : null));
          this.dialog_aprobar = true;
        })
        .finally(() => {
          this.loading_accion = false;
        });
    },
    /**
     * La ejecucion la dispara el modal, que necesita el response para repartir el resultado por
     * linea: por eso devuelve la promesa en vez de resolver todo aca.
     */
    aprobarUbicacion() {
      if (this.ubicacion_a_aprobar == null) return null;

      this.loading_accion = true;
      //finally() deja pasar el response: el modal lo necesita para repartir el detalle
      return this.service.aprobar_ubicacion({ stock_count_location_id: this.ubicacion_a_aprobar.id }).finally(() => {
        this.loading_accion = false;
      });
    },
    /**
     * Se recarga TODO si se llego a aplicar, aunque los ajustes hayan rebotado: la ubicacion se
     * desbloquea antes de ajustar (§7.3.1), asi que su estado y los numeros del reporte cambiaron
     * igual.
     *
     * SIN toast: el resultado se lee en la tabla —el modal lo muestra linea por linea si algo
     * reboto, y si salio todo bien la recarga deja la ubicacion resuelta a la vista—. El cartel
     * con los rechazos concatenados es justo lo que vinimos a sacar.
     */
    cerrarAplicarAjustes(aplicado) {
      this.dialog_aprobar = false;
      this.ubicacion_a_aprobar = null;
      this.lineas_a_aprobar = [];

      //Si se aplico, tambien se cierra el modal de lineas que lo abrio: la ubicacion ya se resolvio
      if (aplicado) {
        this.cerrarUbicacion();
        this.loadAll();
      }
    },

    exportar() {
      this.loading_export = true;
      this.service
        .exportar({ id: this.conteo_id })
        .then((response) => {
          if (response.status) this.descargarExcel(response.result.list);
          else this.toast.error(response.msg);
        })
        .finally(() => {
          this.loading_export = false;
        });
    },
    /**
     * El backend sirve el detalle como JSON, no como archivo: el armado del Excel va acá.
     * Es a propósito — el export del conteo es una tabla plana, y generarla en el navegador
     * evita sumarle un endpoint de archivo al backend para una sola pantalla.
     */
    descargarExcel(lineas) {
      const por_id = {};
      this.ubicaciones.forEach((u) => {
        por_id[u.id] = u.ubicacion ? u.ubicacion.code : "";
      });

      const cabecera = ["Ubicacion", "Producto", "SKU", "LPN", "Atributos", "Teorico", "Contado", "Diferencia", "Estado", "Hallazgo", "Observaciones"];
      //Las cantidades como numero, para que Excel las pueda sumar
      const numero = (v) => (v == null || v === "" ? "" : Number(v));
      const filas = lineas.map((l) => [
        por_id[l.stock_count_location_id] || "",
        this.productoName(l),
        this.productoSku(l),
        l.lpn ? l.lpn.code : "",
        this.atributos(l)
          .map((a) => a.label + ": " + a.value)
          .join(" / "),
        numero(l.theoretical_amount),
        numero(l.counted_amount),
        numero(l.difference),
        l.status ? l.status.name : "",
        l.is_hallazgo ? "Si" : "No",
        (l.observations || "").replace(/\n/g, " / "),
      ]);

      const hoja = XLSX.utils.aoa_to_sheet([cabecera, ...filas]);
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Detalle");
      XLSX.writeFile(libro, "conteo-" + this.conteo_id + ".xlsx");
    },
  },
};
</script>

<style scoped>
/* Todas las cards del reporte con el mismo alto: estiran a la fila y el hint reserva su renglon */
.stat-tile {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  border-radius: 4px;
}

.stat-tile-hint {
  margin-top: auto;
  min-height: 20px;
}

/* v-card-title viene con nowrap + ellipsis: recorta el buscador y el boton de exportar */
.cabecera-tabla {
  white-space: normal;
  overflow: visible;
}

/* Los chips son etiquetas de estado: en bold se leen de un vistazo entre el resto del texto */
:deep(.v-chip) {
  font-weight: bold;
}

/* Como el resto de las tablas del sistema: sin sombra, con el recuadro fino */
.tabla-recuadro {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

/*
  Todo centrado vertical. Las celdas tienen alturas muy distintas —Atributos puede traer seis
  renglones y LPN uno solo—, y alineadas arriba la fila se lee como escalones.
*/
.ubicacion-row > td.col-check {
  width: 48px;
  padding-right: 0;
}
.ubicacion-row > td {
  padding-top: 8px;
  padding-bottom: 8px;
  vertical-align: middle;
}
</style>
